/**
 * Modernisierte Cash-X Sidebar
 * 
 * Design: Glassmorphism, Teal/Emerald Akzente, Lucide Icons
 * Features: Collapsible, Smooth Animations, Touch-friendly
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Bot, BarChart3, ShoppingCart, UtensilsCrossed,
  ClipboardList, Truck, Car, Users, Package, Sparkles, ShoppingBag,
  UserCircle, Mail, Brain, Gift, QrCode, Building2, Palette, Megaphone,
  FileText, FileSpreadsheet, Download, CreditCard, Settings, Receipt,
  Printer, Shield, ChevronLeft, ChevronRight, X, Menu, Ban, Ticket
} from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Icon mapping for menu items
const iconMap = {
  'Dashboard': LayoutDashboard,
  'KI-Tagesassistent': Bot,
  'Smart Analytics': BarChart3,
  'Kasse': ShoppingCart,
  'Tische': UtensilsCrossed,
  'Bestellungen': ClipboardList,
  'Dispatch': Truck,
  'Fahrer-Management': Car,
  'Team-Verwaltung': Users,
  'Produkte': Package,
  'KI-Menü-Import': Sparkles,
  'KI-Einkaufsleiter': ShoppingBag,
  'KI-Gastemanager': UserCircle,
  'KI-Umsatz-Tracking': Mail,
  'KI-Kontrollzentrale': Brain,
  'Kundenbindung': Gift,
  'QR-Bestellung': QrCode,
  'Standorte': Building2,
  'Smart Branding': Palette,
  'KI-Marketing': Megaphone,
  'Berichte': FileText,
  'Steuer-Export': FileSpreadsheet,
  'Exporte': Download,
  'Mein Abo': CreditCard,
  'Einstellungen': Settings,
  'Bon-Einstellungen': Receipt,
  'Print-Agent': Printer,
  'Admin-Bereich': Shield,
  'Bestellungsregeln': Ticket,
  '86\'d Manager': Ban,
  'Gutscheine': Gift,
  'Treueprogramm': Sparkles,
};

function Sidebar({ isOpen, onClose }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  // Check if user is admin
  const userStr = localStorage.getItem('cashx_user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Menu items with paths - SIMPLIFIED FOR GO-LIVE
  // Only essential features enabled, KI features disabled for now
  const menuItems = [
    // === KERN-FUNKTIONEN (Essential) ===
    { path: '/app', label: 'Dashboard', section: 'main' },
    { path: '/app/kassier', label: 'Kasse', section: 'main' },
    { path: '/app/bestelluebersicht', label: 'Bestellungen', section: 'main' },
    { path: '/app/produkte', label: 'Produkte', section: 'main' },
    { type: 'divider' },
    
    // === TEAM & BETRIEB ===
    { path: '/app/team', label: 'Team', section: 'operations' },
    { path: '/app/tische', label: 'Tische', section: 'operations' },
    { type: 'divider' },
    
    // === BERICHTE ===
    { path: '/app/berichte', label: 'Berichte', section: 'reports' },
    { type: 'divider' },
    
    // === EINSTELLUNGEN ===
    { path: '/app/einstellungen', label: 'Einstellungen', section: 'settings' },
    { path: '/app/print-agent', label: 'Drucker', section: 'settings' },
    
    // === ADMIN (nur für Admins) ===
    ...(isAdmin ? [
      { type: 'divider' },
      { path: '/admin', label: 'Admin-Bereich', external: true, section: 'admin' },
    ] : []),
  ];

  const NavItem = ({ item }) => {
    const Icon = iconMap[item.label] || LayoutDashboard;
    const isActive = item.path === '/app' 
      ? location.pathname === '/app' 
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    
    const linkContent = (
      <div
        className={`
          group flex items-center gap-3 px-3 py-2.5 rounded-lg
          transition-all duration-200 cursor-pointer
          ${isActive 
            ? 'bg-gradient-to-r from-teal-500/15 to-emerald-500/10 text-teal-300 border border-teal-400/20 shadow-[0_0_20px_rgba(45,212,191,0.1)]' 
            : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'
          }
          ${collapsed ? 'justify-center px-2' : ''}
        `}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
      >
        <Icon className={`
          w-5 h-5 flex-shrink-0 transition-transform duration-200
          ${isActive ? 'text-teal-400' : 'text-slate-500 group-hover:text-slate-300'}
          ${!collapsed ? 'group-hover:-translate-y-[1px]' : ''}
        `} />
        {!collapsed && (
          <span className={`text-sm font-medium truncate ${isActive ? 'text-teal-200' : ''}`}>
            {item.label}
          </span>
        )}
        {isActive && !collapsed && (
          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
        )}
      </div>
    );

    if (collapsed) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              {item.external ? (
                <a href={item.path} className="block mb-1">
                  {linkContent}
                </a>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => window.innerWidth < 768 && onClose()}
                  className="block mb-1"
                >
                  {linkContent}
                </Link>
              )}
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-[#111C32] border-slate-700 text-slate-200">
              {item.label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (item.external) {
      return (
        <a href={item.path} className="block mb-1">
          {linkContent}
        </a>
      );
    }

    return (
      <Link
        to={item.path}
        onClick={() => window.innerWidth < 768 && onClose()}
        className="block mb-1"
      >
        {linkContent}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-50
          bg-[#111C32]/95 backdrop-blur-xl
          border-r border-slate-700/50
          transition-all duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          ${collapsed ? 'w-[72px]' : 'w-64'}
        `}
        style={{ 
          boxShadow: '4px 0 30px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div className={`
          h-[80px] flex items-center
          border-b border-slate-700/50
          bg-gradient-to-r from-[#0B1220] to-[#111C32]
          ${collapsed ? 'justify-center px-2' : 'justify-between px-4'}
        `}>
          {!collapsed ? (
            <div className="flex flex-col gap-1.5 w-full">
              <img 
                src="/cash-x-logo.png" 
                alt="Cash-X" 
                className="h-10 w-auto object-contain"
              />
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider">
                  TSE-konform
                </span>
              </div>
            </div>
          ) : (
            <img 
              src="/cash-x-logo.png" 
              alt="Cash-X" 
              className="h-8 w-auto object-contain"
            />
          )}

          {/* Collapse Toggle - Desktop */}
          <button
            data-testid="sidebar-toggle-button"
            onClick={() => setCollapsed(!collapsed)}
            className={`
              hidden md:flex items-center justify-center
              absolute -right-3 top-1/2 -translate-y-1/2
              w-6 h-6 rounded-full
              bg-[#111C32] border border-slate-600/50
              text-slate-400 hover:text-white hover:border-teal-400/50
              transition-all duration-200 hover:scale-110
              shadow-lg
            `}
          >
            {collapsed ? (
              <ChevronRight className="w-3.5 h-3.5" />
            ) : (
              <ChevronLeft className="w-3.5 h-3.5" />
            )}
          </button>

          {/* Mobile Close */}
          <button
            onClick={onClose}
            className="md:hidden p-2 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="h-[calc(100%-80px)]">
          <nav className={`py-4 ${collapsed ? 'px-2' : 'px-3'}`}>
            {menuItems.map((item, index) => {
              if (item.type === 'divider') {
                return (
                  <div
                    key={`divider-${index}`}
                    className={`
                      h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent
                      my-3 ${collapsed ? 'mx-1' : 'mx-2'}
                    `}
                  />
                );
              }

              return <NavItem key={item.path} item={item} />;
            })}
          </nav>
        </ScrollArea>

        {/* Footer - User Info (when expanded) */}
        {!collapsed && user && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-700/50 bg-[#0B1220]/80 backdrop-blur">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500/30 to-emerald-500/30 flex items-center justify-center border border-teal-400/20">
                <span className="text-sm font-medium text-teal-300">
                  {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {user.name || user.email}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {user.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Spacer for content */}
      <div 
        className={`hidden md:block flex-shrink-0 transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'}`} 
      />
    </>
  );
}

export default Sidebar;
